import React, { useState, useMemo, useEffect } from 'react';
import { MdChevronLeft, MdChevronRight, MdPerson } from 'react-icons/md';
import './AirbnbCalendar.css';

const isOwnerBlockedDate = (b) =>
  Boolean(b?.isBlockedDate) || String(b?.status || '').toUpperCase() === 'BLOCKED';

const isCancelledBooking = (b) => {
  const s = (b.status || '').toUpperCase();
  return ['CANCELLED', 'REJECTED'].includes(s);
};

const isBookingActive = (b) => {
  if (isOwnerBlockedDate(b)) return false;
  return !isCancelledBooking(b);
};

const AirbnbCalendar = ({
  bookings = [],
  properties = [],
  onAction = () => { },
  formatMoneyKES = (val) => val,
  formatDateStr = (val) => val,
  propertyById = {},
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tablePage, setTablePage] = useState(1);
  const pageSize = 15;

  // Reset table page on month change
  useEffect(() => {
    setTablePage(1);
  }, [currentDate]);

  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month + 1, 0);
    return date.getDate();
  }, [currentDate]);

  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const year = currentDate.getFullYear();

  // Calendar Grid Calculations (42 cells: 6 weeks * 7 days)
  const calendarGrid = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 (Sun) to 6 (Sat)
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const grid = [];

    // Previous Month Days (Padding to reach Sunday)
    for (let i = 0; i < firstDayOfMonth; i++) {
      const day = daysInPrevMonth - (firstDayOfMonth - 1 - i);
      grid.push({
        day,
        month: month - 1,
        year,
        isCurrentMonth: false,
        date: new Date(year, month - 1, day)
      });
    }

    // Current Month Days
    for (let i = 1; i <= daysInMonth; i++) {
      grid.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }

    // Next Month Days (Fill remaining of 42 cells)
    const remaining = 42 - grid.length;
    for (let i = 1; i <= remaining; i++) {
      grid.push({
        day: i,
        month: month + 1,
        year,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }
    return grid;
  }, [currentDate, daysInMonth]);

  // Chunk grid into weeks (Strictly 7 days per row)
  const weeks = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < calendarGrid.length; i += 7) {
      chunks.push(calendarGrid.slice(i, i + 7));
    }
    return chunks;
  }, [calendarGrid]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // All units across all properties or selected property
  const allUnits = useMemo(() => {
    const units = [];
    properties.forEach(prop => {
      if (prop.units && Array.isArray(prop.units)) {
        prop.units.forEach(unit => {
          units.push({
            ...unit,
            propertyName: prop.name,
            propertyId: prop.id
          });
        });
      }
    });
    return units;
  }, [properties]);

  // Filter bookings for the period covered by the grid
  const calendarBookings = useMemo(() => {
    const startOfGrid = calendarGrid[0].date;
    const endOfGrid = new Date(calendarGrid[calendarGrid.length - 1].date);
    endOfGrid.setHours(23, 59, 59, 999);

    return bookings.filter(b => {
      const checkIn = new Date(b.checkInDate);
      const checkOut = new Date(b.checkOutDate);
      return (checkIn <= endOfGrid && checkOut >= startOfGrid);
    });
  }, [bookings, calendarGrid]);

  const totalPages = Math.ceil(calendarBookings.length / pageSize);

  const getStatusColor = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'BLOCKED') return '#64748b';
    if (['CONFIRMED', 'ASSIGNED', 'CHECKED_IN', 'CHECKED_OUT', 'MANUAL'].includes(s)) return 'var(--status-confirmed)';
    if (['PENDING', 'REQUESTED', 'APPROVED'].includes(s)) return 'var(--status-pending)';
    if (['CANCELLED', 'REJECTED'].includes(s)) return 'var(--status-cancelled)';
    return 'var(--status-other)';
  };

  // Logic to calculate bar appearance for each week
  const getWeekBars = (week) => {
    const weekStart = new Date(week[0].date);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(week[6].date);
    weekEnd.setHours(23, 59, 59, 999);

    const bars = [];
    calendarBookings.forEach(b => {
      const start = new Date(b.checkInDate);
      const end = new Date(b.checkOutDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      if (start <= weekEnd && end >= weekStart) {
        // Calculate overlap with this week
        const overlapStart = start < weekStart ? weekStart : start;
        const overlapEnd = end > weekEnd ? weekEnd : end;
        
        const colStart = overlapStart.getDay(); // 0-6
        // To cover both start and end days: (Days Diff) + 1
        const d1 = new Date(overlapStart); d1.setHours(0,0,0,0);
        const d2 = new Date(overlapEnd); d2.setHours(0,0,0,0);
        const span = Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;

        bars.push({
          booking: b,
          colStart,
          span,
          isStart: start >= weekStart,
          isEnd: end <= weekEnd
        });
      }
    });

    // Assign tracks (stacking)
    const tracks = [];
    bars.sort((a, b) => b.span - a.span); // Longest first for better packing

    bars.forEach(bar => {
      let trackIndex = 0;
      while (true) {
        if (!tracks[trackIndex]) {
          tracks[trackIndex] = [bar];
          bar.track = trackIndex;
          break;
        }
        const hasOverlap = tracks[trackIndex].some(t =>
          (bar.colStart < (t.colStart + t.span)) && ((bar.colStart + bar.span) > t.colStart)
        );
        if (!hasOverlap) {
          tracks[trackIndex].push(bar);
          bar.track = trackIndex;
          break;
        }
        trackIndex++;
      }
    });

    return bars;
  };

  // Stats for Sidebar
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activeBookings = calendarBookings.filter(isBookingActive);

    const occupiedTodayCount = activeBookings.filter(b => {
      const start = new Date(b.checkInDate);
      const end = new Date(b.checkOutDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    }).length;

    return {
      totalUnits: allUnits.length,
      monthlyBookings: activeBookings.length,
      occupiedToday: occupiedTodayCount,
      percentOccupied: allUnits.length > 0 ? (occupiedTodayCount / allUnits.length) * 100 : 0
    };
  }, [allUnits, calendarBookings]);

  return (
    <div className="airbnb-calendar">
      <div className="airbnb-calendar__layout">
        <div className="airbnb-calendar__main">
          {/* Header */}
          <div className="airbnb-calendar__header">
            <div className="airbnb-calendar__nav">
              <button onClick={handlePrevMonth} className="airbnb-calendar__nav-btn"><MdChevronLeft /></button>
              <h2 className="airbnb-calendar__month-title">{monthName} {year}</h2>
              <button onClick={handleNextMonth} className="airbnb-calendar__nav-btn"><MdChevronRight /></button>
            </div>
            <div className="airbnb-calendar__header-actions">
              <button onClick={handleToday} className="airbnb-calendar__today-btn">Today</button>
            </div>
          </div>

          {/* Grid Container (Week-based rows) */}
          <div className="airbnb-calendar__month-grid-container">
            <div className="airbnb-calendar__weekdays-header">
              {weekDays.map(wd => (
                <div key={wd} className="airbnb-calendar__weekday-label">{wd}</div>
              ))}
            </div>

            <div className="airbnb-calendar__month-grid">
              {weeks.map((week, weekIdx) => {
                const weekBars = getWeekBars(week);
                const maxTrack = weekBars.reduce((max, b) => Math.max(max, b.track || 0), -1);
                
                // Adaptive spacing: squeeze on mobile, space out on laptop
                const isSmall = typeof window !== 'undefined' && window.innerWidth < 768;
                const trackHeight = isSmall ? 22 : 32;
                const topOffset = isSmall ? 25 : 35;
                const rowHeight = Math.max(isSmall ? 80 : 110, (maxTrack + 1) * trackHeight + topOffset + 10);

                return (
                  <div key={weekIdx} className="airbnb-calendar__week-row" style={{ minHeight: `${rowHeight}px` }}>
                    <div className="airbnb-calendar__day-cells-bg">
                      {week.map((cell, dayIdx) => {
                        const isToday = new Date().toDateString() === cell.date.toDateString();
                        return (
                          <div 
                            key={dayIdx} 
                            className={`airbnb-calendar__day-cell ${cell.isCurrentMonth ? '' : 'is-prev-next'} ${isToday ? 'is-today' : ''}`}
                          >
                            <div className="airbnb-calendar__day-header">
                              <span className="airbnb-calendar__day-num">{cell.day}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="airbnb-calendar__bars-overlay">
                      {weekBars.map((bar, barIdx) => {
                        const isOwnerBlocked = isOwnerBlockedDate(bar.booking);
                        const isCancelled = !isOwnerBlocked && isCancelledBooking(bar.booking);
                        const left = (bar.colStart / 7) * 100;
                        const width = (bar.span / 7) * 100;
                        const top = bar.track * trackHeight + topOffset;
                        const unitLabel = bar.booking.unitName || bar.booking.unitNumber || 'Unit';

                        return (
                          <div
                            key={`${weekIdx}-${barIdx}`}
                            className={`airbnb-calendar__spanning-bar${isOwnerBlocked ? ' is-owner-blocked' : ''}${isCancelled ? ' is-blocked' : ''}`}
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                              top: `${top}px`,
                              backgroundColor: getStatusColor(bar.booking.status),
                            }}
                            title={
                              isOwnerBlocked
                                ? `Blocked — ${unitLabel}${bar.booking.reason ? `: ${bar.booking.reason}` : ''}`
                                : isCancelled
                                  ? 'Canceled'
                                  : `${bar.booking.guestName} ${bar.booking.bookingCode ? `(${bar.booking.bookingCode})` : ''} - ${unitLabel}`
                            }
                          >
                            <span className="airbnb-calendar__badge-guest">
                              {isOwnerBlocked
                                ? 'Blocked'
                                : isCancelled
                                  ? 'Canceled'
                                  : `${bar.booking.guestName} ${bar.booking.bookingCode ? `(${bar.booking.bookingCode})` : ''}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="airbnb-calendar__sidebar">
          <h3 className="airbnb-calendar__sidebar-title">Availability Overview</h3>
          <div className="airbnb-calendar__availability-stats">
            <div className="airbnb-calendar__stat-item">
              <span className="airbnb-calendar__stat-label">Total Units</span>
              <span className="airbnb-calendar__stat-value">{stats.totalUnits}</span>
            </div>
            <div className="airbnb-calendar__stat-item">
              <span className="airbnb-calendar__stat-label">Guests Today</span>
              <span className="airbnb-calendar__stat-value">{stats.occupiedToday}</span>
            </div>
          </div>

          <div className="airbnb-calendar__stat-summary">
            <div className="airbnb-calendar__stat-bar-label">
              <span>Month Total</span>
              <span>{stats.monthlyBookings} Bookings</span>
            </div>
          </div>

          <div className="airbnb-calendar__property-list">
            {properties.map(prop => {
              const propUnits = prop.units || [];
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const occupiedToday = calendarBookings.filter(b => {
                if (b.propertyId !== prop.id) return false;
                if (!isBookingActive(b)) return false;
                const start = new Date(b.checkInDate);
                const end = new Date(b.checkOutDate);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return today >= start && today <= end;
              }).length;

              const availCount = Math.max(0, propUnits.length - occupiedToday);
              const progress = propUnits.length > 0 ? (availCount / propUnits.length) * 100 : 0;

              return (
                <div key={prop.id} className="airbnb-calendar__prop-item">
                  <div className="airbnb-calendar__prop-header">
                    <span className="airbnb-calendar__prop-name">{prop.name}</span>
                    <span className="airbnb-calendar__prop-count">{availCount}/{propUnits.length} available</span>
                  </div>
                  <div className="airbnb-calendar__progress-bg">
                    <div className="airbnb-calendar__progress-bar" style={{ width: `${progress}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Guest Table */}
      <div className="airbnb-calendar__guest-table-section">
        <h3 className="airbnb-calendar__table-title">Guest Details ({calendarBookings.length})</h3>
        <div className="airbnb-calendar__table-wrapper">
          <table className="airbnb-calendar__table">
            <thead>
              <tr>
                <th>Guest</th>
                <th>Property/Unit</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const startIndex = (tablePage - 1) * pageSize;
                const paginatedBookings = calendarBookings.slice(startIndex, startIndex + pageSize);

                if (paginatedBookings.length === 0) {
                  return (
                    <tr>
                      <td colSpan="5" className="airbnb-calendar__table-empty">No bookings for this month</td>
                    </tr>
                  );
                }

                return paginatedBookings.map((b) => {
                  const ownerBlocked = isOwnerBlockedDate(b);
                  return (
                    <tr key={b.id}>
                    <td>
                      <div className="airbnb-calendar__guest-cell">
                        <div className="airbnb-calendar__avatar"><MdPerson /></div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{ownerBlocked ? (b.reason || 'Blocked') : (b.guestName || 'Guest')}</span>
                          {!ownerBlocked && b.bookingCode && (
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#0284c7' }}>{b.bookingCode}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="airbnb-calendar__prop-cell">
                        <span className="airbnb-calendar__table-prop">{b.propertyName || propertyById[b.propertyId]}</span>
                        <span className="airbnb-calendar__table-unit">{b.unitName || b.unitNumber}</span>
                      </div>
                    </td>
                    <td>
                      <div className="airbnb-calendar__date-cell">
                        {formatDateStr(b.checkInDate)} - {formatDateStr(b.checkOutDate)}
                      </div>
                    </td>
                    <td>
                      <span className={`airbnb-calendar__status-badge airbnb-calendar__status-badge--${(b.status || '').toLowerCase()}`}>
                        {b.status}
                      </span>
                    </td>
                    <td>
                      <div className="airbnb-calendar__payment-cell">
                        {ownerBlocked ? '—' : formatMoneyKES(b.totalAmount)}
                      </div>
                    </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {calendarBookings.length > pageSize && (
          <div className="airbnb-calendar__pagination">
            <button
              className="airbnb-calendar__page-btn"
              onClick={() => setTablePage(p => Math.max(1, p - 1))}
              disabled={tablePage === 1}
            >
              <MdChevronLeft /> Prev
            </button>
            <span className="airbnb-calendar__page-info">
              Page {tablePage} of {totalPages}
            </span>
            <button
              className="airbnb-calendar__page-btn"
              onClick={() => setTablePage(p => Math.min(totalPages, p + 1))}
              disabled={tablePage === totalPages}
            >
              Next <MdChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AirbnbCalendar;
